import React, { Component } from "react";

class FileField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedFile: null,
      preview: null,
      isDragOver: false,
      isLoading: false,
      isDeleting: false,
    };
    this.fileInputRef = React.createRef();
    this.dropZoneRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    // Sync with form state when it changes externally
    // This is important after file upload when the value changes from File object to file info object
    if (this.props.defaultValue !== prevProps.defaultValue && this.props.defaultValue) {

      this.setState({ selectedFile: this.props.defaultValue, isLoading: false });
    }
  }

  // Drag and drop handlers
  handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragOver to false if we're leaving the drop zone entirely
    if (this.dropZoneRef.current && !this.dropZoneRef.current.contains(e.relatedTarget)) {
      this.setState({ isDragOver: false });
    }
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: false });

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      this.processFile(files[0]);
    }
  };

  // File processing
  processFile = async (file) => {
    this.setState({ isLoading: true, selectedFile: file });

    try {
      // Update react-hook-form state with file object initially
      this.props.setValue(this.props.name, [file.id], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });

      // Auto-upload file if uploadFile function is provided
      if (this.props.uploadFile && file) {
        const uploadResult = await this.props.uploadFile(file, this.props.name);
        // Update form state with uploaded file info (array with id)
        this.props.setValue(this.props.name, [uploadResult.id], {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      }

      // Create preview for images and documents
      if (file) {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.setState({ preview: e.target.result, isLoading: false });
          };
          reader.onerror = () => {
            this.setState({ isLoading: false });
          };
          reader.readAsDataURL(file);
        } else {
          // For non-image files, create a generic preview
          this.setState({
            preview: this.getFileIcon(file.type),
            isLoading: false
          });
        }
      } else {
        this.setState({ preview: null, isLoading: false });
      }
    } catch (error) {
      this.setState({ isLoading: false });
      // Show upload error if available
      if (this.props.uploadError) {
        // Error is handled by the parent component
      }
    }
  };

  handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      this.processFile(file);
    }
  };

  handleRemoveFile = async () => {
    const { selectedFile } = this.state;
    const { deleteFile, name, setValue } = this.props;

    // Extract file ID - could be a number, string, or object with id property
    // The new format is { id: number, name: string }
    let fileId = null;
    if (selectedFile) {
      if (typeof selectedFile === 'number') {
        fileId = selectedFile;
      } else if (typeof selectedFile === 'string') {
        fileId = selectedFile;
      } else if (typeof selectedFile === 'object' && selectedFile.id) {
        fileId = selectedFile.id;
      }
    }

    // If we have an ID and deleteFile function, delete it from the server
    if (deleteFile && fileId) {

      this.setState({ isDeleting: true });
      try {
        await deleteFile(fileId, name);

      } catch (error) {

        this.setState({ isDeleting: false });
        return;
      }
    } else {

    }

    this.setState({
      selectedFile: null,
      preview: null,
      isLoading: false,
      isDeleting: false
    });
    setValue(name, null, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
  };

  // Get file icon based on file type
  getFileIcon = (fileType) => {
    // You can return SVG icons or use a icon library
    return (
      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
      </div>
    );
  };

  // Format file size
  formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  render() {
    const {
      register,
      errors,
      label,
      name,
      rules,
      className,
      labelClassName,
      labelStyles,
      accept,
      multiple = false,
      isUploading = false,
      uploadError,
    } = this.props;

    const { selectedFile, preview, isDragOver, isLoading, isDeleting } = this.state;

    return (
      <div className={`flex flex-col items-start ${className}`}>
        <label
          className={`form-label font-bold float-start mb-1 ${labelClassName}`}
          style={labelStyles}
        >
          {rules?.required && <span className="text-red-500">*</span>} {label}
        </label>

        <div className="w-full">
          {selectedFile ? (
            /* File Selected State */
            <div className="border-2 border-gray-300 bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {typeof preview === 'string' && preview.startsWith('data:image') ? (
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                      {preview}
                    </div>
                  )}
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">
                      {typeof selectedFile === 'object' && selectedFile.name
                        ? selectedFile.name
                        : typeof selectedFile === 'object' && selectedFile.id
                          ? `Archivo #${selectedFile.id}`
                          : `Archivo #${selectedFile}`}
                    </p>
                    {typeof selectedFile === 'object' && selectedFile.size && (
                      <p className="text-xs text-gray-500">
                        {this.formatFileSize(selectedFile.size)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(isLoading || isUploading) && (
                    <div className="flex flex-col items-center space-y-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                      <span className="text-xs text-gray-600">Uploading...</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={this.handleRemoveFile}
                    disabled={isDeleting || isUploading}
                    className="text-red-500 hover:text-red-700 p-2 transition-colors disabled:opacity-50 relative z-20"
                    title="Remove file"
                  >
                    {isDeleting ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Drop Zone */
            <div
              ref={this.dropZoneRef}
              className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${isDragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
                }`}
              onDragEnter={this.handleDragEnter}
              onDragLeave={this.handleDragLeave}
              onDragOver={this.handleDragOver}
              onDrop={this.handleDrop}
            >
              {/* Upload Area */}
              <div className="space-y-3">
                <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium text-blue-500 hover:text-blue-600 cursor-pointer">
                      Click to upload
                    </span>{' '}
                    or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {accept ? `Supported formats: ${accept}` : 'All file types supported'}
                  </p>
                </div>
              </div>

              {/* Hidden File Input */}
              <input
                ref={this.fileInputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={this.handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
          )}

          {/* Hidden input for react-hook-form */}
          <input
            type="hidden"
            {...register(name, rules)}
          />
        </div>

        {(errors[name] || uploadError) && (
          <span className="text-red-500 text-sm mt-1">
            {errors[name]?.message || uploadError}
          </span>
        )}
      </div>
    );
  }
}

export default FileField;